/**
 * Weather forecast on Map – planning (dispatcher app): the weather_* keys of the client config map.
 * Limits are stored in SI units (m/s, mm, °C, %); the wind unit only changes how wind is shown and entered.
 * The provider (Google Weather API) and its API key are backend settings, never part of the config map.
 */
export type WeatherWindUnit = 'ms' | 'kmh';

export class WeatherConfig {
  enabled = false;
  forecastDays = 5;
  windUnit: WeatherWindUnit = 'ms';
  /** Max gust 07–19 (m/s) */
  gustWarn = 10;
  gustStop = 15;
  /** Precipitation 07–19 (mm) */
  rainWarn = 1;
  rainStop = 5;
  /** Day's low, 07:00 → 07:00 next morning (°C) */
  tempLowWarn = 0;
  tempLowStop = -5;
  /** Day's high (°C) */
  tempHighWarn = 30;
  tempHighStop = 35;
  /** Thunderstorm or freezing rain 07–19 → Stop */
  stormStop = true;
  /** Thunderstorm probability (%) that counts as a thunderstorm */
  stormProb = 40;

  static fromMap(map: Map<string, string>): WeatherConfig {
    const config = new WeatherConfig();
    config.enabled = bool(map.get('weather_enabled'), config.enabled);
    config.forecastDays = num(map.get('weather_forecast_days'), config.forecastDays);
    config.windUnit = map.get('weather_wind_unit') === 'kmh' ? 'kmh' : 'ms';
    config.gustWarn = num(map.get('weather_gust_warn'), config.gustWarn);
    config.gustStop = num(map.get('weather_gust_stop'), config.gustStop);
    config.rainWarn = num(map.get('weather_rain_warn'), config.rainWarn);
    config.rainStop = num(map.get('weather_rain_stop'), config.rainStop);
    config.tempLowWarn = num(map.get('weather_temp_low_warn'), config.tempLowWarn);
    config.tempLowStop = num(map.get('weather_temp_low_stop'), config.tempLowStop);
    config.tempHighWarn = num(map.get('weather_temp_high_warn'), config.tempHighWarn);
    config.tempHighStop = num(map.get('weather_temp_high_stop'), config.tempHighStop);
    config.stormStop = bool(map.get('weather_storm_stop'), config.stormStop);
    config.stormProb = num(map.get('weather_storm_prob'), config.stormProb);
    return config;
  }

  writeTo(map: Map<string, string>): void {
    map.set('weather_enabled', String(this.enabled));
    map.set('weather_forecast_days', String(this.forecastDays));
    map.set('weather_wind_unit', this.windUnit);
    map.set('weather_gust_warn', str(this.gustWarn));
    map.set('weather_gust_stop', str(this.gustStop));
    map.set('weather_rain_warn', str(this.rainWarn));
    map.set('weather_rain_stop', str(this.rainStop));
    map.set('weather_temp_low_warn', str(this.tempLowWarn));
    map.set('weather_temp_low_stop', str(this.tempLowStop));
    map.set('weather_temp_high_warn', str(this.tempHighWarn));
    map.set('weather_temp_high_stop', str(this.tempHighStop));
    map.set('weather_storm_stop', String(this.stormStop));
    map.set('weather_storm_prob', String(this.stormProb));
  }
}

function bool(value: string | undefined, fallback: boolean): boolean {
  return value === 'true' ? true : value === 'false' ? false : fallback;
}

function num(value: string | undefined, fallback: number): number {
  const parsed = value != null && value.trim() !== '' ? Number(value.replace(',', '.')) : NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** At most two decimals, no trailing zeros (km/h entries converted to m/s, e.g. 50 km/h → "13.89"). */
function str(value: number): string {
  return String(+value.toFixed(2));
}
